// Google Analytics 4 (gtag.js) — thin wrapper that turns into a no-op
// when VITE_GA4_MEASUREMENT_ID isn't set, so dev / staging / local
// runs never pollute the production analytics property.
//
// Why direct gtag instead of Google Tag Manager: one analytics
// destination, ~25 KB gz async-loaded, no GTM UI to learn or break.
// If RanaVolt adds ad pixels (Meta, TikTok) later, swap this module
// out for a GTM container — every call site here calls one of the
// `track*` helpers, never `gtag` directly, so the migration is local
// to this file.
//
// Page views are NOT auto-fired by gtag config — main.ts hooks
// router.afterEach so SPA navigation triggers page_view correctly
// (gtag's built-in send_page_view only fires once per script load).
//
// Admin / login pages don't get analytics because they're served from
// public/*.html and don't load this module. /products and /products/:slug
// (the Vue SPA) are where everything happens.

// Minimal product shape this module needs. Defined here (not imported
// from features/products) so shared/ stays one-way-dependent on
// features/ per CLAUDE.md §3 — features pass their richer Product into
// these helpers via TypeScript structural typing.
export interface AnalyticsProduct {
  id?: number | string;
  slug?: string;
  sku?: string;
  name: string;
  regular_price?: string;
  categories?: { name: string }[];
}

const MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as
  | string
  | undefined;

interface GtagFn {
  (command: "config", id: string, params?: Record<string, unknown>): void;
  (command: "event", name: string, params?: Record<string, unknown>): void;
  (command: "set", params: Record<string, unknown>): void;
  (command: "js", date: Date): void;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

let initialised = false;

/**
 * Inject the gtag.js script and configure GA4. Safe to call multiple
 * times — only the first call does any work. No-op when no
 * measurement ID is set.
 */
export function initAnalytics(): void {
  if (initialised || !MEASUREMENT_ID || typeof window === "undefined") return;
  initialised = true;

  // gtag's bootstrap pattern — push to dataLayer so commands queued
  // before the script loads still flush correctly.
  window.dataLayer = window.dataLayer || [];
  const gtag: GtagFn = function (...args: unknown[]) {
    window.dataLayer!.push(args);
  } as GtagFn;
  window.gtag = gtag;

  gtag("js", new Date());
  // send_page_view: false — router.afterEach owns SPA page_view firing.
  gtag("config", MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);
}

/**
 * Generic event dispatcher. Use the typed helpers below when one
 * fits — they keep the GA4 ecommerce schema correct. This is the
 * escape hatch for events without a typed wrapper.
 */
export function trackEvent(
  name: string,
  params: Record<string, unknown> = {},
): void {
  if (!MEASUREMENT_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
}

export function trackPageView(path: string, title?: string): void {
  trackEvent("page_view", {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

// ─── GA4 ecommerce events ────────────────────────────────────────
// All ecommerce events use the same `items` shape — { item_id,
// item_name, price, currency, ...}. Helper below normalises a
// Product into that shape so the call sites stay readable.

interface Ga4Item {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  price?: number;
  currency: string;
  quantity?: number;
  item_list_id?: string;
  item_list_name?: string;
}

function productToItem(product: AnalyticsProduct): Ga4Item {
  const price = Number(product.regular_price);
  return {
    item_id: product.sku || String(product.id ?? product.slug),
    item_name: product.name,
    item_brand: "RanaVolt",
    item_category: product.categories?.[0]?.name,
    ...(price > 0 ? { price } : {}),
    currency: "NGN",
    quantity: 1,
  };
}

export function trackViewItem(product: AnalyticsProduct): void {
  const item = productToItem(product);
  trackEvent("view_item", {
    currency: "NGN",
    value: item.price ?? 0,
    items: [item],
  });
}

export function trackViewItemList(
  products: AnalyticsProduct[],
  listName = "Products catalogue",
): void {
  trackEvent("view_item_list", {
    item_list_name: listName,
    items: products.map((p, index) => ({
      ...productToItem(p),
      index,
      item_list_name: listName,
    })),
  });
}

export function trackSelectItem(
  product: AnalyticsProduct,
  listName = "Products catalogue",
): void {
  trackEvent("select_item", {
    item_list_name: listName,
    items: [{ ...productToItem(product), item_list_name: listName }],
  });
}

/**
 * Lead-style conversion — fired when a customer signals intent to
 * buy without going through a checkout (WhatsApp CTA, configurator
 * submit, contact form). `source` is the surface the lead came from
 * so the GA4 funnel can split conversion rates by channel.
 */
export function trackGenerateLead(
  source: "whatsapp" | "configurator" | "contact-form" | "phone",
  options: { value?: number; productName?: string } = {},
): void {
  trackEvent("generate_lead", {
    currency: "NGN",
    ...(options.value ? { value: options.value } : {}),
    method: source,
    ...(options.productName ? { product_name: options.productName } : {}),
  });
}
